import React, { useEffect, useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  ListTodo,
  MessageSquare,
  Search,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { cn } from '../lib/utils';
import { useToastUIStore } from '../store/toastUIStore';
import { useInitialLoadingScreenManager } from '../hooks/useInitialLoadingScreenManager';
import { useSmartRedirect } from '../hooks/useSmartRedirect';
import { AIModelConfig } from '../components/admin/AIModelConfig';
import { NoteSummaryBatchJob } from '../components/admin/NoteSummaryBatchJob';
import { ChatMessageSummaryBatchJob } from '../components/admin/ChatMessageSummaryBatchJob';
import { TaskManagement } from '../components/admin/TaskManagement';

interface UserDetails {
  id: string;
  email: string;
  display_name: string | null;
  last_sign_in_at: string | null;
  is_admin: boolean;
  is_vip: boolean;
  subscription_status: string | null;
  subscription_tier: string | null;
  subscription_interval: string | null;
  projects_count: number;
  customer_stripe_id: string | null;
}

export const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { getSmartRedirectPath } = useSmartRedirect();
  const [users, setUsers] = useState<UserDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'ai-models' | 'notes' | 'chat' | 'tasks'>(
    'users'
  );

  // Sorting states
  type SortField =
    | 'display_name'
    | 'last_sign_in_at'
    | 'projects_count'
    | 'subscription_tier'
    | 'is_vip';
  const [sortField, setSortField] = useState<SortField>('display_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Get loading screen manager - must be called at component level
  const { markRouteComplete } = useInitialLoadingScreenManager();
  const hasMarkedCompleteRef = React.useRef(false);

  useEffect(() => {

    const fetchUsers = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session) {
          throw new Error('No active session');
        }

        // Check admin status for debug purposes
        console.log('Current user ID:', session.user.id);
        console.log('Current user email:', session.user.email);

        // Make direct RPC call to check admin status
        const { data: isAdmin, error: adminCheckError } = await supabase.rpc('is_admin', {
          user_id: session.user.id,
        });

        console.log('Direct admin check result:', isAdmin);
        if (adminCheckError) {
          console.error('Error in direct admin check:', adminCheckError);
        }

        // Use the edge function to fetch all user details
        console.log('Fetching user details from edge function...');

        const { data, error } = await supabase.functions.invoke(
          'get-all-users-with-subscriptions-and-projects',
          {
            method: 'POST',
          }
        );

        if (error) {
          console.error('Error fetching users:', error);
          throw error;
        }

        console.log('Users data:', data);
        setUsers(data || []);
      } catch (err) {
        console.error('Error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch users';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    void fetchUsers();
  }, []);

  // Mark route as complete when loading is done
  useEffect(() => {
    if (!loading && !hasMarkedCompleteRef.current) {
      hasMarkedCompleteRef.current = true;
      markRouteComplete();
    }
  }, [loading, markRouteComplete]);

  const toggleVipStatus = async (userId: string, currentVipStatus: boolean) => {
    const { addToast } = useToastUIStore.getState();

    try {
      // Get current user's ID for the admin parameter
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Could not get current user ID');
      }

      if (currentVipStatus) {
        // Revoke VIP status
        const { error } = await supabase.rpc('revoke_vip_status', {
          target_user_id: userId,
          admin_user_id: user.id,
        });

        if (error) {
          throw error;
        }
      } else {
        // Grant VIP status
        const { error } = await supabase.rpc('grant_vip_status', {
          target_user_id: userId,
          admin_user_id: user.id,
          expiration_date: null,
        });

        if (error) {
          throw error;
        }
      }

      // Update local state
      setUsers(
        users.map(user => (user.id === userId ? { ...user, is_vip: !currentVipStatus } : user))
      );

      addToast({
        type: 'success',
        message: `${currentVipStatus ? 'Removed' : 'Assigned'} VIP status`,
      });
    } catch (error) {
      console.error('Error toggling VIP status:', error);
      addToast({
        type: 'error',
        message: 'Failed to change VIP status',
      });
    }
  };

  // Handler for changing sort field and direction
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter users based on search query
  const getFilteredUsers = () => {
    if (!searchQuery.trim()) {
      return users; // Return all users if no search query
    }

    const query = searchQuery.toLowerCase().trim();

    return users.filter(user => {
      const userName = (user.display_name || '').toLowerCase();
      const userEmail = user.email.toLowerCase();

      return userName.includes(query) || userEmail.includes(query);
    });
  };

  // Get sorted users based on current sort settings
  const getSortedUsers = () => {
    return [...getFilteredUsers()].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      // Get values based on sort field
      switch (sortField) {
        case 'display_name':
          // Fall back to email if display_name is not available
          aValue = (a.display_name || a.email).toLowerCase();
          bValue = (b.display_name || b.email).toLowerCase();
          break;
        case 'last_sign_in_at':
          aValue = a.last_sign_in_at ? new Date(a.last_sign_in_at).getTime() : 0;
          bValue = b.last_sign_in_at ? new Date(b.last_sign_in_at).getTime() : 0;
          break;
        case 'projects_count':
          aValue = a.projects_count;
          bValue = b.projects_count;
          break;
        case 'subscription_tier': {
          // Custom sorting order for subscription tiers
          const tierOrder = {
            free: 0,
            pro: 1,
            team_small: 2,
            team_medium: 3,
            team_large: 4,
            vip: 5,
          };
          aValue = tierOrder[a.subscription_tier as keyof typeof tierOrder] ?? 999;
          bValue = tierOrder[b.subscription_tier as keyof typeof tierOrder] ?? 999;
          break;
        }
        case 'is_vip':
          aValue = a.is_vip ? 1 : 0;
          bValue = b.is_vip ? 1 : 0;
          break;
        default:
          aValue = (a.display_name || a.email).toLowerCase();
          bValue = (b.display_name || b.email).toLowerCase();
      }

      // Apply sort direction
      const sortModifier = sortDirection === 'asc' ? 1 : -1;

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * sortModifier;
      }

      // Handle number comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * sortModifier;
      }

      // Fallback for mixed types or unexpected scenarios
      return 0;
    });
  };

  const handleGoBack = async () => {
    // Use smart redirect to determine the best destination
    const redirectPath = await getSmartRedirectPath();
    navigate(redirectPath);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="relative flex items-center p-4 bg-gray-900 border-b border-gray-800">
        <button
          onClick={handleGoBack}
          className="absolute left-4 p-2 hover:bg-gray-800 rounded-full transition-colors"
        >
          <ChevronLeft className="h-6 w-6 text-white" />
        </button>
        <h1 className="text-2xl font-bold flex-grow text-center text-white">Admin Dashboard</h1>
      </div>

      <div className="container mx-auto p-6">
        {error && (
          <div className="bg-red-900/20 border border-red-500 text-red-400 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Tab navigation */}
        <div className="flex border-b border-gray-700 mb-6 flex-wrap">
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
            onClick={() => setActiveTab('users')}
          >
            <Users size={16} className="inline mr-2" />
            Users
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'ai-models'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
            onClick={() => setActiveTab('ai-models')}
          >
            <Settings size={16} className="inline mr-2" />
            AI Models
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'tasks'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
            onClick={() => setActiveTab('tasks')}
          >
            <ListTodo size={16} className="inline mr-2" />
            Task Management
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'notes'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
            onClick={() => setActiveTab('notes')}
          >
            <MessageSquare size={16} className="inline mr-2" />
            Note Summaries
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'chat'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
            onClick={() => setActiveTab('chat')}
          >
            <MessageSquare size={16} className="inline mr-2" />
            Chat Summaries
          </button>
        </div>

        {activeTab === 'users' ? (
          <>
            <div className="mb-4 relative">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full py-2 px-4 pl-10 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search size={16} className="text-gray-400" />
                </div>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                    aria-label="Clear search"
                  >
                    &times;
                  </button>
                )}
              </div>
              {searchQuery.trim() !== '' && (
                <div className="mt-2 text-sm text-gray-400">
                  Showing {getFilteredUsers().length} of {users.length} users
                </div>
              )}
            </div>
            <div className="bg-gray-800 rounded-2xl overflow-hidden shadow-2xl border border-gray-700">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-900 text-gray-400 uppercase text-sm leading-normal">
                    <th
                      className="py-3 px-6 text-left cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('display_name')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>User</span>
                        {sortField === 'display_name' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          ))}
                      </div>
                    </th>
                    <th
                      className="py-3 px-6 text-left cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('last_sign_in_at')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Last Sign In</span>
                        {sortField === 'last_sign_in_at' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          ))}
                      </div>
                    </th>
                    <th
                      className="py-3 px-6 text-left cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('projects_count')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Projects</span>
                        {sortField === 'projects_count' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          ))}
                      </div>
                    </th>
                    <th
                      className="py-3 px-6 text-left cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('subscription_tier')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Subscription</span>
                        {sortField === 'subscription_tier' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          ))}
                      </div>
                    </th>
                    <th
                      className="py-3 px-6 text-left cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('is_vip')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>VIP</span>
                        {sortField === 'is_vip' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          ))}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-white text-sm">
                  {getSortedUsers().map(user => (
                    <tr
                      key={user.id}
                      className="border-b border-gray-700 hover:bg-gray-900/50 transition-colors"
                    >
                      <td className="py-3 px-6">
                        <div className="flex flex-col">
                          <span className="font-medium">{user.display_name || user.email}</span>
                          {user.display_name && (
                            <span className="text-gray-400 text-xs">{user.email}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-6">{formatDate(user.last_sign_in_at)}</td>
                      <td className="py-3 px-6">
                        <span className="px-3 py-1 rounded-full text-xs bg-blue-900/20 text-blue-400">
                          {user.projects_count}
                        </span>
                      </td>
                      <td className="py-3 px-6">
                        <div className="flex items-center space-x-2">
                          <span
                            className={cn(
                              'inline-flex px-2 py-0.5 rounded-full text-xs',
                              user.subscription_status === 'active' || user.is_vip
                                ? 'bg-green-900/20 text-green-400'
                                : 'bg-gray-900/20 text-gray-400'
                            )}
                          >
                            {user.is_vip
                              ? 'VIP'
                              : user.subscription_status === 'active'
                                ? (() => {
                                    // Format subscription tier for display using standardized values
                                    switch (user.subscription_tier) {
                                      case 'pro':
                                        return 'Pro';
                                      case 'team_small':
                                        return 'Team (S)';
                                      case 'team_medium':
                                        return 'Team (M)';
                                      case 'team_large':
                                        return 'Team (L)';
                                      case 'free':
                                        return 'Free';
                                      default:
                                        return user.subscription_tier || 'Unknown';
                                    }
                                  })()
                                : 'Free'}
                          </span>
                          {user.subscription_status === 'active' && user.subscription_interval && (
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-blue-900/20 text-blue-400">
                              {user.subscription_interval === 'month' ? 'Monthly' : 'Annual'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-6">
                        <button
                          onClick={() => toggleVipStatus(user.id, user.is_vip)}
                          className={cn(
                            'flex items-center justify-center w-8 h-8 rounded-full transition-colors',
                            user.is_vip
                              ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                              : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'
                          )}
                          title={user.is_vip ? 'Remove VIP Status' : 'Make VIP'}
                        >
                          <ShieldCheck size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : activeTab === 'ai-models' ? (
          <AIModelConfig />
        ) : activeTab === 'tasks' ? (
          <TaskManagement />
        ) : activeTab === 'notes' ? (
          <NoteSummaryBatchJob />
        ) : activeTab === 'chat' ? (
          <ChatMessageSummaryBatchJob />
        ) : (
          <div className="p-4 bg-gray-800 border border-gray-700 rounded-md text-gray-400">
            Unknown tab selected
          </div>
        )}
      </div>
    </div>
  );
};
